import React, { useState } from 'react';
import { API_BASE } from '../config/apiConfig';
import DatabaseMetadataBanner from './DatabaseMetadataBanner';

/**
 * LLM Module — renders the appropriate business intelligence view
 * based on which sidebar menu was clicked.
 *
 * Each "mode" corresponds to an LLM use case defined in the client's LLM config.
 * Queries are routed through the LLM DB Connector which enforces:
 *   - Tenant isolation via app.current_tenant_id (RLS)
 *   - Read-only access (no INSERT/UPDATE/DELETE)
 *   - PII redaction before LLM processing
 */

const MODULE_CONFIG = {
  // Restaurant / Hospitality
  orders:       { icon: '🍽️', title: 'Orders & Sales',      hint: 'Ask about daily sales, revenue, top items, P&L reports' },
  inventory:    { icon: '📦', title: 'Inventory',            hint: 'Check stock levels, reorder points, waste logs, vendor orders' },
  reservations: { icon: '📅', title: 'Reservations',         hint: 'View upcoming reservations, table availability, guest history' },
  catering:     { icon: '🎪', title: 'Catering & Events',    hint: 'Manage catering orders, event bookings, venue rentals' },
  staff:        { icon: '👥', title: 'Staff',                hint: 'Schedules, attendance, overtime, staffing levels' },
  // Fleet / Logistics
  fleet:        { icon: '🚛', title: 'Fleet Tracker',        hint: 'Vehicle status, location, fuel consumption' },
  routes:       { icon: '🗺️', title: 'Routes',               hint: 'Route optimization, delivery schedules, ETAs' },
  drivers:      { icon: '👥', title: 'Drivers',              hint: 'Driver assignments, hours, performance' },
  maintenance:  { icon: '🔧', title: 'Maintenance',          hint: 'Vehicle maintenance schedules, repair history' },
  // Healthcare
  patients:     { icon: '🏥', title: 'Patients',             hint: 'Patient records, visit history, care plans' },
  appointments: { icon: '📅', title: 'Appointments',         hint: 'Scheduling, availability, reminders' },
  records:      { icon: '📋', title: 'Records',              hint: 'Medical records, lab results, prescriptions' },
  billing:      { icon: '💳', title: 'Billing',              hint: 'Invoices, payments, insurance claims' },
  // Retail
  pos:          { icon: '🛒', title: 'Point of Sale',        hint: 'Transactions, receipts, daily totals' },
  customers:    { icon: '👥', title: 'Customers',            hint: 'Customer profiles, loyalty points, purchase history' },
  promotions:   { icon: '🏷️', title: 'Promotions',           hint: 'Active promotions, coupon usage, campaign performance' },
  // Generic
  operations:   { icon: '⚙️', title: 'Operations',           hint: 'General operational queries against your database' },
  data:         { icon: '📊', title: 'Business Data',        hint: 'Query any business data in your database' },
  reports:      { icon: '📋', title: 'Reports',              hint: 'Generate reports from your operational data' },
  // Chain
  chain:        { icon: '🏗️', title: 'Chain Overview',       hint: 'Aggregated data across all locations (parent view)' },
  // Core
  askme:        { icon: '💬', title: 'Ask Me (LLM)',        hint: 'Ask any question about your business — powered by your private LLM layer' },
  command:      { icon: '🎛️', title: 'Command Center',       hint: 'System configuration, AI layer management, operator controls' },
};

const GenAIModule = ({ clientConfig, mode }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  const config = MODULE_CONFIG[mode] || MODULE_CONFIG.askme;

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setAnswer(null);

    try {
      // In production, this calls the LLM query endpoint which routes through
      // the LLM router with tenant-scoped DB access and PII redaction
      const res = await fetch(`${API_BASE}/clients/${clientConfig?.client_id}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          context: mode,
          tenant_id: clientConfig?.client_id,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setAnswer(data);
      } else {
        // Show a helpful message when the endpoint isn't deployed yet
        setAnswer({
          answer: `Your question "${question}" would be processed by the LLM layer against your private database using tenant-isolated views. The query context is "${mode}".`,
          model: 'preview',
          tool_used: 'query_database',
          note: 'LLM query endpoint not yet connected — this is a preview of the request flow.',
        });
      }
    } catch {
      setAnswer({
        answer: `Query preview for "${mode}" context: "${question}"`,
        note: 'Backend not reachable — showing preview mode.',
      });
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>{config.icon} {config.title}</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        {config.hint}
      </p>

      <form onSubmit={handleAsk} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <input
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder={`Ask about ${config.title.toLowerCase()}...`}
          style={{
            flex: 1, padding: '0.75rem 1rem', borderRadius: '8px',
            border: '1px solid #ddd', fontSize: '0.95rem',
          }}
          aria-label={`Ask a question about ${config.title}`}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none',
            background: '#2c3e50', color: '#fff', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {loading ? 'Thinking...' : 'Ask GenAI'}
        </button>
      </form>

      {answer && (
        <div>
          {/* Database Metadata Banner at Top */}
          <DatabaseMetadataBanner
            dbMetadata={answer.db_metadata}
            rowCount={answer.row_count || answer.rows_scanned || 0}
            isHardcoded={!answer.db_metadata && answer.source !== 'llm_sql'}
            position="top"
          />

          <div style={{
            background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem',
          }}>
            {answer.note && (
              <div style={{
                background: '#fff3e0', padding: '0.5rem 1rem', borderRadius: '6px',
                fontSize: '0.8rem', color: '#e65100', marginBottom: '1rem',
              }}>
                ℹ️ {answer.note}
              </div>
            )}
            <div style={{ lineHeight: 1.7, color: '#333' }}>{answer.answer}</div>
            {answer.model && (
              <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#aaa' }}>
                Model: {answer.model} {answer.tool_used ? `· Tool: ${answer.tool_used}` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {!answer && (
        <div style={{
          background: '#f8f9fa', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', color: '#aaa', border: '1px solid #e0e0e0',
        }}>
          <p>Ask a question to query your private database through the GenAI layer.</p>
          <p style={{ fontSize: '0.8rem' }}>
            All queries are scoped to your tenant via Row-Level Security.
            Only read-only access is permitted. PII is automatically redacted.
          </p>
        </div>
      )}
    </div>
  );
};

export default GenAIModule;
